import { useMemo, useState } from 'react';

import { UsePaginationProps, UsePaginationResult } from './pagination.definitions';

export const dots = '...';

function buildRange(start: number, end: number): number[] {
  const length = end - start + 1;
  return Array.from({ length }, (_, i) => i + start);
}

export const usePagination = ({
  totalPages,
  pageSiblingCount = 1,
  currentPage,
}: UsePaginationProps): UsePaginationResult => {
  // This controls the pages for scrolling and displaying purposes
  const [activePage, setActivePage] = useState(currentPage);

  const paginationRange: (number | '...')[] | null = useMemo(() => {
    const totalPageNumbers = pageSiblingCount + 5;

    // If the totalPages is less than the page numbers we want to show we return the range [1..totalPageCount]
    // 10 total pages and we are on page 5 => show page 1, page 4, page 5, page 6, page 7, page 10
    if (totalPageNumbers >= totalPages) {
      return buildRange(1, totalPages);
    }

    const leftSiblingIndex = Math.max(activePage - pageSiblingCount, 1);
    const rightSiblingIndex = Math.min(activePage + pageSiblingCount, totalPages);

    // No shouldShowLeftDots if the first page is visible
    // No shouldShowRightDots if the last page is visible
    // Show dots when there are pageSiblingCount + 3 pages between the first and last page
    const shouldShowLeftDots = leftSiblingIndex > 2;
    const shouldShowRightDots = rightSiblingIndex < totalPages - 2;

    const firstPageIndex = 1;
    const lastPageIndex = totalPages;

    if (!shouldShowLeftDots && shouldShowRightDots) {
      const leftItemCount = 3 + 2 * pageSiblingCount;
      const leftRange = buildRange(1, leftItemCount);

      return [...leftRange, dots, totalPages];
    }

    if (shouldShowLeftDots && !shouldShowRightDots) {
      const rightItemCount = 3 + 2 * pageSiblingCount;
      const rightRange = buildRange(totalPages - rightItemCount + 1, totalPages);
      return [firstPageIndex, dots, ...rightRange];
    }

    if (shouldShowLeftDots && shouldShowRightDots) {
      const middleRange = buildRange(leftSiblingIndex, rightSiblingIndex);
      return [firstPageIndex, dots, ...middleRange, dots, lastPageIndex];
    }
    return null;
  }, [totalPages, pageSiblingCount, activePage]);

  return { paginationRange, activePage, setActivePage };
};
